import { products, getProductBySlug } from "../data/products.js";

const SITE_URL = "https://grsoftmedia.com.do";
/** @typedef {import("../data/products.js").Product} Product */
/** @typedef {{title: string, description: string, path: string, robots?: string}} PageMeta */
/** @typedef {{type: "home"} | {type: "product", product: Product} | {type: "request-received"} | {type: "not-found"}} Route */

/** @type {PageMeta} */
const homeMeta = {
  title: "GR SoftMedia | Software empresarial en República Dominicana",
  description: "GR SoftMedia desarrolla software empresarial para administración, proyectos y obras, facturación electrónica y automatización en República Dominicana.",
  path: "/",
};

/** @type {PageMeta} */
const requestReceivedMeta = {
  title: "Solicitud recibida | GR SoftMedia",
  description: "Confirmación de solicitud de demostración de GR SoftMedia.",
  path: "/solicitud-recibida",
  robots: "noindex,follow",
};

/** @param {PageMeta} meta */
function setMeta(meta) {
  document.title = meta.title;
  const url = `${SITE_URL}${meta.path}`;
  const selectors = {
    'meta[name="description"]': meta.description,
    'meta[property="og:title"]': meta.title,
    'meta[property="og:description"]': meta.description,
    'meta[property="og:url"]': url,
    'meta[name="twitter:title"]': meta.title,
    'meta[name="twitter:description"]': meta.description,
  };
  Object.entries(selectors).forEach(([selector, content]) => document.querySelector(selector)?.setAttribute("content", content));
  document.querySelector('meta[name="robots"]')?.setAttribute("content", meta.robots ?? "index,follow,max-image-preview:large");
  document.querySelector('link[rel="canonical"]')?.setAttribute("href", url);
}

/** @param {string} pathname @returns {PageMeta} */
function metaForPath(pathname) {
  const normalized = pathname.replace(/\/+$/, "") || "/";
  if (normalized === requestReceivedMeta.path) return requestReceivedMeta;
  const slug = normalized.match(/^\/(?:productos\/)?([^/]+)$/)?.[1];
  const product = slug ? getProductBySlug(slug) : null;
  if (!product) return homeMeta;
  return {
    title: `${product.seoTitle} | GR SoftMedia`,
    description: product.seoDescription,
    path: product.route,
    robots: normalized.startsWith("/productos/") ? "noindex,follow" : undefined,
  };
}

/** @param {string} pathname @returns {Route} */
export function resolveRoute(pathname) {
  const normalized = pathname.replace(/\/+$/, "") || "/";
  if (normalized === "/" || normalized === "/productos" || normalized === "/productos.html") return { type: "home" };
  if (normalized === "/solicitud-recibida") return { type: "request-received" };
  const slug = normalized.match(/^\/(?:productos\/)?([^/]+)$/)?.[1];
  const product = slug ? getProductBySlug(slug) : null;
  return product ? { type: "product", product } : { type: "not-found" };
}

/** @param {string} href @param {() => void} render */
export function navigate(href, render) {
  const url = new URL(href, window.location.origin);
  const currentPath = window.location.pathname;
  if (url.pathname !== currentPath) history.pushState({}, "", `${url.pathname}${url.hash}`);
  else if (url.hash) history.replaceState({}, "", `${url.pathname}${url.hash}`);
  render();
}

/** @param {() => void} render */
export function bindRouter(render) {
  document.addEventListener("click", (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const anchor = target.closest("a[data-route]");
    if (!anchor || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const href = anchor.getAttribute("href");
    if (!href) return;
    event.preventDefault();
    navigate(href, render);
  });
  window.addEventListener("popstate", render);
}

export function updatePageMeta() {
  setMeta(metaForPath(window.location.pathname));
}

export const productRoutes = products.map((product) => product.route);
